<template>
  <div class="voucher-list">
    <div class="bg-white">
      <div class="p-6 shadow-border-b">
        <h1 class="text-2xl font-bold text-neutral-700">{{ $t('voucher.title') }}</h1>
        <p class="text-sm text-neutral-500 mt-2">{{ $t('voucher.subtitle') }}</p>
      </div>

      <!-- Search Bar -->
      <div class="p-6 border-b border-gray-200">
        <div class="max-w-md">
          <label for="search" class="block text-sm font-medium text-gray-700 mb-2">
            {{ $t('voucher.searchLabel') }}
          </label>
          <div class="relative">
            <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <input
              id="search"
              v-model="searchQuery"
              @input="handleSearch"
              type="text"
              :placeholder="$t('voucher.searchPlaceholder')"
              class="block w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500 text-sm"
            />
            <div v-if="searchQuery" class="absolute inset-y-0 right-0 pr-3 flex items-center">
              <button @click="clearSearch" class="text-gray-400 hover:text-gray-600 transition-colors">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Voucher List -->
      <div class="p-6">
        <div v-if="loading" class="flex justify-center items-center py-20">
          <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
        </div>

        <div v-else-if="voucherList.items.length === 0" class="text-center py-20">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-24 w-24 mx-auto mb-4 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z"
            />
          </svg>
          <p class="text-lg font-medium text-gray-700">{{ $t('voucher.emptyStateTitle') }}</p>
          <p class="text-sm text-gray-500 mt-2">{{ $t('voucher.emptyStateSubtitle') }}</p>
        </div>

        <div v-else class="overflow-x-auto">
          <table class="min-w-full bg-white border border-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-b">
                  {{ $t('voucher.table.courier') }}
                </th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-b">
                  {{ $t('voucher.table.voucherName') }}
                </th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-b">
                  {{ $t('voucher.table.voucherCode') }}
                </th>
                <th class="px-6 py-3 text-center text-xs font-medium text-gray-700 uppercase tracking-wider border-b">
                  {{ $t('voucher.table.discount') }}
                </th>
                <th class="px-6 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider border-b">
                  {{ $t('voucher.table.maxDiscount') }}
                </th>
                <th class="px-6 py-3 text-center text-xs font-medium text-gray-700 uppercase tracking-wider border-b">
                  {{ $t('voucher.table.quota') }}
                </th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-b">
                  {{ $t('voucher.table.period') }}
                </th>
                <th class="px-6 py-3 text-center text-xs font-medium text-gray-700 uppercase tracking-wider border-b">
                  {{ $t('voucher.table.action') }}
                </th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-200">
              <tr v-for="voucher in voucherList.items" :key="voucher.id" class="hover:bg-gray-50 transition-colors">
                <!-- Delivery Service -->
                <td class="px-6 py-4 whitespace-nowrap">
                  <div class="flex items-center gap-3">
                    <div class="flex-shrink-0 w-10 h-10 bg-cyan-100 rounded flex items-center justify-center overflow-hidden">
                      <img
                        v-if="voucher.delivery_service && voucher.delivery_service.logo"
                        :src="voucher.delivery_service.logo"
                        :alt="voucher.delivery_service.name"
                        class="w-full h-full object-contain"
                        @error="$event.target.style.display = 'none'"
                      />
                      <svg v-else xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-cyan-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
                      </svg>
                    </div>
                    <div>
                      <p class="text-sm font-medium text-gray-900" v-if="voucher.delivery_service">
                        {{ voucher.delivery_service.name }}
                      </p>
                      <p class="text-sm text-gray-500" v-else>
                        {{ $t('voucher.allCourier') }}
                      </p>
                    </div>
                  </div>
                </td>

                <!-- Voucher Name -->
                <td class="px-6 py-4">
                  <p class="text-sm font-semibold text-gray-900">{{ voucher.name }}</p>
                </td>

                <!-- Voucher Code -->
                <td class="px-6 py-4 whitespace-nowrap">
                  <span class="inline-flex items-center px-3 py-1 rounded-md text-sm font-mono font-bold bg-gray-100 text-gray-900 border border-gray-300">
                    {{ voucher.voucher_code }}
                  </span>
                </td>

                <!-- Discount Percentage -->
                <td class="px-6 py-4 whitespace-nowrap text-center">
                  <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-semibold bg-green-100 text-green-800"> {{ voucher.discount_percentage }}% </span>
                </td>

                <!-- Max Discount Amount -->
                <td class="px-6 py-4 whitespace-nowrap text-right">
                  <p class="text-sm font-medium text-gray-900" v-if="voucher.max_discount_amount">
                    {{ formatCurrency(voucher.max_discount_amount) }}
                  </p>
                  <p class="text-sm text-gray-400" v-else>
                    -
                  </p>
                </td>

                <!-- Quota -->
                <td class="px-6 py-4 whitespace-nowrap text-center">
                  <div class="text-sm">
                    <span class="font-bold text-blue-600">{{ voucher.available_quota }}</span>
                    <span class="text-gray-400">/</span>
                    <span class="text-gray-600">{{ voucher.quota }}</span>
                  </div>
                  <div class="w-full bg-gray-200 rounded-full h-1.5 mt-1">
                    <div class="bg-blue-600 h-1.5 rounded-full" :style="{ width: `${(voucher.available_quota / voucher.quota) * 100}%` }"></div>
                  </div>
                </td>

                <!-- Period -->
                <td class="px-6 py-4">
                  <div class="text-sm text-gray-700">
                    <div class="flex items-center gap-1 mb-1">
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <span class="text-xs text-gray-500">{{ $t('voucher.periodStart') }}</span>
                      <span class="text-xs">{{ formatDate(voucher.start_date) }}</span>
                    </div>
                    <div class="flex items-center gap-1">
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <span class="text-xs text-gray-500">{{ $t('voucher.periodEnd') }}</span>
                      <span class="text-xs">{{ formatDate(voucher.end_date) }}</span>
                    </div>
                  </div>
                </td>

                <!-- Action -->
                <td class="px-6 py-4 whitespace-nowrap text-center">
                  <button
                    @click="goToOrderForm(voucher)"
                    class="inline-flex items-center gap-2 px-4 py-2 bg-primary-500 text-white text-sm font-medium rounded-lg hover:bg-primary-600 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                    </svg>
                    {{ $t('voucher.orderButton') }}
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Pagination -->
        <div v-if="voucherList.total_pages > 1" class="mt-8 flex justify-center items-center gap-2">
          <button
            @click="goToPage(voucherList.page - 1)"
            :disabled="voucherList.page === 1"
            class="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {{ $t('voucher.pagination.previous') }}
          </button>

          <div class="flex gap-2">
            <template v-for="(page, index) in visiblePages">
              <span v-if="page === '...'" :key="`ellipsis-${index}`" class="px-4 py-2 text-gray-400">
                ...
              </span>
              <button
                v-else
                :key="page"
                @click="goToPage(page)"
                :class="[
                  'px-4 py-2 border rounded-lg transition-colors',
                  page === voucherList.page ? 'bg-primary-500 text-white border-primary-500' : 'border-gray-300 hover:bg-gray-50 text-gray-700'
                ]"
              >
                {{ page }}
              </button>
            </template>
          </div>

          <button
            @click="goToPage(voucherList.page + 1)"
            :disabled="voucherList.page === voucherList.total_pages"
            class="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {{ $t('voucher.pagination.next') }}
          </button>
        </div>

        <!-- Pagination Info -->
        <div v-if="voucherList.total_items > 0" class="mt-4 text-center text-sm text-gray-600">
          {{ $t('voucher.pagination.showing') }} {{ (voucherList.page - 1) * voucherList.limit + 1 }} {{ $t('voucher.pagination.to') }}
          {{ Math.min(voucherList.page * voucherList.limit, voucherList.total_items) }}
          {{ $t('voucher.pagination.of') }} {{ voucherList.total_items }} {{ $t('voucher.pagination.vouchers') }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'

export default {
  name: 'VoucherList',
  data() {
    return {
      loading: false,
      itemsPerPage: 12,
      searchQuery: '',
      searchTimeout: null
    }
  },
  computed: {
    ...mapGetters('merchant', ['voucherList']),
    currentPage() {
      return this.voucherList.page || 1
    },
    visiblePages() {
      const total = this.voucherList.total_pages
      const current = this.currentPage
      const delta = 2
      const pages = []

      // Add first page
      if (current > delta + 1) {
        pages.push(1)
        if (current > delta + 2) {
          pages.push('...')
        }
      }

      // Add pages around current
      for (let i = Math.max(1, current - delta); i <= Math.min(total, current + delta); i++) {
        pages.push(i)
      }

      // Add last page
      if (current < total - delta) {
        if (current < total - delta - 1) {
          pages.push('...')
        }
        pages.push(total)
      }

      return pages
    }
  },
  created() {
    this.loadVouchers(1)
  },
  methods: {
    ...mapActions('merchant', ['getVouchersPagination']),
    ...mapActions('common', ['showLoading', 'hideLoading']),
    loadVouchers(page = 1) {
      this.loading = true
      const params = {
        page: page,
        limit: this.itemsPerPage
      }

      // Add search parameter if searchQuery is not empty
      if (this.searchQuery && this.searchQuery.trim()) {
        params.search = this.searchQuery.trim()
      }

      this.getVouchersPagination({ params })
        .then(() => {
          this.loading = false
        })
        .catch(error => {
          console.error('Error loading vouchers:', error)
          this.loading = false
        })
    },
    goToPage(page) {
      if (page === '...' || page < 1 || page > this.voucherList.total_pages) return
      this.loadVouchers(page)
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },
    handleSearch() {
      // Clear existing timeout
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout)
      }

      // Debounce search - wait 500ms after user stops typing
      this.searchTimeout = setTimeout(() => {
        this.loadVouchers(1)
      }, 500)
    },
    clearSearch() {
      this.searchQuery = ''
      this.loadVouchers(1)
    },
    goToOrderForm(voucher) {
      // Redirect to order form with voucher data
      this.$router.push({
        path: '/order/step/shipping'
      })
    },
    formatCurrency(value) {
      if (!value) return 'Rp 0'
      return 'Rp ' + value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')
    },
    formatDate(dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      const options = { day: 'numeric', month: 'short', year: 'numeric' }
      return date.toLocaleDateString('id-ID', options)
    }
  }
}
</script>

<style scoped>
.voucher-list {
  min-height: 100vh;
  background-color: #f9fafb;
}
</style>
